import { srmAsset, srmVideo } from '../assets';
import type { SrmScene, SrmSceneKey } from '../types';

/** 二级页（/product/ai-srm/scene/:sceneKey）六个智慧场景内容 */
const sceneList: SrmScene[] = [
  {
    key: 'collective-procurement',
    order: 1,
    name: '数智化集采',
    cardTitle: '统一集采·议价提升',
    subtitle: '从「各自为政」到「统一协同」',
    summary: '建立「一个平台、两种模式」的采购治理模式',
    hero: {
      url: srmAsset('scene/collective-procurement/hero.webp'),
      alt: '数智化集采场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/collective-procurement.webp'),
      alt: '采购负责人通过数据看板分析统一集采与议价提升空间'
    },
    cardTags: ['集采执行看板', '集采节约分析', '集采建议洞察'],
    cardVariant: 'collective',
    values: [
      { title: '一个平台、两种模式', desc: '集中采购与分散采购并行，兼顾统一治理与下级组织自主性。', icon: 'platform' },
      { title: '通用物资集中采购', desc: '集团统一寻源、形成框架协议，各下级组织按需下单。', icon: 'procurement' },
      { title: '专业物资分散采购', desc: '下级组织自主寻源，集团设定规则并进行监督。', icon: 'material' },
      { title: '释放规模议价能力', desc: '通用物资集中采购可释放 15–25% 议价空间。', icon: 'bargaining' }
    ],
    apps: ['集采执行看板', '集采节约分析', '集采建议洞察'],
    process: {
      url: srmAsset('scene/collective-procurement/process.webp'),
      alt: '数智化集采业务流程'
    },
    processMobile: {
      url: srmAsset('scene/collective-procurement/mobile-process.webp'),
      alt: '数智化集采业务流程（移动端）'
    },
    demos: [
      {
        title: 'AI需求预测与计划建议',
        lead: '通过预测参数配置与需求分析，结合库存情况形成预测结果和补货建议。',
        bullets: ['配置预测参数，发起需求预测', '结合库存水位，分析需求与补货量', '查看预测结果与 AI 分析报告'],
        value: '为需求统筹与采购计划提供依据',
        media: { type: 'video', url: srmVideo('demand-forecast') }
      },
      {
        title: 'AI集采机会洞察与建议',
        lead: '通过AI管理驾驶舱，实时掌握采购运行情况，为集采机会预判提供依据。',
        bullets: ['跟进进展，AI问数探寻策略', '经营看板呈现采购数据与运行情况', '智能生成专项分析，辅助研判集采机会'],
        value: '让集采决策有数据、有分析',
        media: { type: 'video', url: srmVideo('centralized-sourcing-insight') }
      }
    ]
  },
  {
    key: 'full-process',
    order: 2,
    name: '数智化全流程',
    cardTitle: '数智采购·全链贯通',
    subtitle: '招标延伸，数智化支撑全链路',
    summary: '从「只管招标」升级为「管好采购全链路」：需求→寻源→合同→执行→结算',
    hero: {
      url: srmAsset('scene/full-process/hero.webp'),
      alt: '数智化全流程场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/full-process.webp'),
      alt: '采购与会人员在现代会议室查阅采购数据分析与合同文件'
    },
    cardTags: ['智能需求识别', '智能寻源推荐', '智能合同比对', '发票智能识别'],
    cardVariant: 'chain',
    values: [
      {
        title: '采购全链路贯通',
        desc: '需求计划、寻源管理、合同／框架、商城、订单、履约与支付连续协同。',
        icon: 'procurement'
      },
      { title: '供应商资源统一连接', desc: '连接合作、潜在、战略、渠道与第三方供应商资源。', icon: 'supplier' },
      { title: '业务执行在线协同', desc: '支撑快速上品、多方式下单、履约协同与自动对账结算。', icon: 'collaboration' },
      { title: '采购经营数据可视', desc: '统一分析采购支出、成本、流程、品质与交付效率。', icon: 'visibility' }
    ],
    apps: ['智能需求识别', '智能寻源推荐', '智能合同比对', '供应商风险查验', '发票智能识别'],
    process: {
      url: srmAsset('scene/full-process/process.webp'),
      alt: '数智化全流程业务流程'
    },
    processMobile: {
      url: srmAsset('scene/full-process/mobile-process.webp'),
      alt: '数智化全流程业务流程（移动端）'
    },
    demos: [
      {
        title: 'AI交付追踪与风险预警',
        lead: '贯通申请、订单、到货与验收节点，可视化追踪交付进度，识别履约风险并给出预警建议。',
        bullets: ['智能串联交付链路，查看各节点进展', '识别延期、异常到货及供应商风险', '输出预警与建议，辅助交付跟进'],
        value: '让交付全程可视，风险提前发现',
        media: { type: 'video', url: srmVideo('delivery-tracking') }
      },
      {
        title: 'AI发票识别与结算核对',
        lead: '自动识别发票关键信息，关联采购订单与收货、验收记录，辅助结算核对。',
        bullets: ['智能提取抬头、税号、金额与税率', '智能匹配订单与收验货记录，标记差异', '汇集核对依据，支撑后续对账付款'],
        value: '减少人工核对，让结算有据可查',
        media: { type: 'video', url: srmVideo('invoice-settlement') }
      }
    ]
  },
  {
    key: 'risk-control',
    order: 3,
    name: '智能风险控制',
    cardTitle: '合规内控·全程追溯',
    subtitle: '从「制度管」到「制度+系统管」',
    summary: '让不合规的操作「做不了」而非仅靠「不敢做」',
    hero: {
      url: srmAsset('scene/risk-control/hero.webp'),
      alt: '智能风险控制场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/risk-control.webp'),
      alt: '采购合规团队与业务伙伴在会议中确认合作与内控要求'
    },
    cardTags: ['围串标识别', '智能清标', '辅助智能评标', '供应商风险查验'],
    cardVariant: 'compliance',
    values: [
      { title: '职责分离', desc: '系统强制「申请≠审批≠采购≠验收≠付款」。', icon: 'segregation-of-duties' },
      { title: '利益冲突申报', desc: '采购人员参与项目前需线上申报利益冲突。', icon: 'conflict-of-interest' },
      { title: '投标信息加密', desc: '开标前投标信息不可查看，保障公平。', icon: 'encryption' },
      { title: '评标全程透明', desc: '线上评审、自动打分、评委独立、结果留痕。', icon: 'bid-transparency' },
      { title: '过程留痕可溯', desc: '每个操作均留完整审计日志，一键出合规报告。', icon: 'audit-trail' },
      { title: '智能风险预警', desc: '围串标分析、采购行为风险识别、自动化内控闭环。', icon: 'risk-alert' }
    ],
    apps: ['智能清标', '围串标识别', '辅助智能评标', '供应商风险查验'],
    process: {
      url: srmAsset('scene/risk-control/process.webp'),
      alt: '智能风险控制业务流程'
    },
    processMobile: {
      url: srmAsset('scene/risk-control/mobile-process.webp'),
      alt: '智能风险控制业务流程（移动端）'
    },
    demos: [
      {
        title: 'AI寻源推荐与招标发起',
        lead: '围绕采购需求推荐供应商，生成招标公告与文件，辅助核验关键内容。',
        bullets: ['AI寻源、智能匹配需求、推荐供应商', 'AI生成招标公告，关键字段校验', 'AI编制招标文件，辅助自检与人工确认'],
        value: '减少招标准备重复工作与内容遗漏',
        media: { type: 'video', url: srmVideo('sourcing-tender') }
      },
      {
        title: 'AI清标评标与合规研判',
        lead: '结合智能清标、围串标分析与辅助评标，识别投标异常，归集评审依据，支持合规研判。',
        bullets: ['AI清标与报价异常识别', 'AI围串标识别与证据链', 'AI多维评分，生成复核建议'],
        value: '让异常有据可查，评审有据可依',
        media: { type: 'video', url: srmVideo('bid-evaluation') }
      },
      {
        title: '合同AI起草与变更管控',
        lead: '结合采购结果与模板辅助起草合同，分析条款风险、比对版本差异，为合同审核提供依据。',
        bullets: ['智能合同起草与风险审查', '分析关键条款，提示履约与合规风险', '智能合同比对，标记差异及风险改动'],
        value: '让合同审核更高效，变更更清晰',
        media: { type: 'video', url: srmVideo('contract-drafting') }
      }
    ]
  },
  {
    key: 'material-governance',
    order: 4,
    name: '智能物料治理',
    cardTitle: '品类治理·标准管控',
    subtitle: '品类繁杂，标准化管理 + 商城化采购',
    summary: '品类管理体系 + 商城电商化体验 + 智能物料治理',
    hero: {
      url: srmAsset('scene/material-governance/hero.webp'),
      alt: '智能物料治理场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/material-governance.webp'),
      alt: '采购团队在物料样品区核对品类标准与智能治理方案'
    },
    cardTags: ['智能物料治理', '相似识别', '合并去重', '一料一码'],
    cardVariant: 'category',
    values: [
      {
        title: '品类管理体系标准化',
        desc: '建立品类分类原则、管理流程、管理规则与优化机制。',
        icon: 'standardization'
      },
      { title: '商城电商化体验', desc: '高频低值物资进入商城目录，支撑快速上品与便捷下单。', icon: 'e-commerce' },
      { title: '智能物料治理', desc: '推动分类合理、属性标准、描述规范、编码统一与一料一码。', icon: 'governance' },
      {
        title: '多样化采购链路',
        desc: '中频中值采用协议采购与框架合同，低频高值进入正式寻源招标。',
        icon: 'diversity'
      }
    ],
    apps: ['增量防控', '存量治理', '品质治理', '重复治理'],
    process: {
      url: srmAsset('scene/material-governance/process.webp'),
      alt: '智能物料治理业务流程'
    },
    processMobile: {
      url: srmAsset('scene/material-governance/mobile-process.webp'),
      alt: '智能物料治理业务流程（移动端）'
    },
    demos: [
      {
        title: '物料主数据AI全域治理',
        lead: '通过新增物料防控、存量筛查与治理工作台，规范物料分类、属性和编码，支撑一料一码。',
        bullets: ['新增物料校验，智能识别相似与重复', '存量物料筛查，发现错码与规格缺失', '统一开展质量与重复治理'],
        value: '统一物料标准，夯实采购数据基础',
        media: { type: 'video', url: srmVideo('material-master-governance') }
      }
    ]
  },
  {
    key: 'supplier-management',
    order: 5,
    name: '供应商智能管理',
    cardTitle: '供应商池·统一共用',
    subtitle: '供应商全生命周期，集团统一视角',
    summary: '一商一档 + 全组织共用 + 绩效驱动',
    hero: {
      url: srmAsset('scene/supplier-management/hero.webp'),
      alt: '供应商智能管理场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/supplier-management.webp'),
      alt: '仓储人员通过移动终端统一查看供应商与仓储数据'
    },
    cardTags: ['智能入驻', '智能风险查验', '智能 360 画像', '智能健康度'],
    cardVariant: 'supplier',
    values: [
      { title: '一商一档', desc: '统一归集资质、风险、品类、交易、绩效与档案资讯。', icon: 'supplier-profile' },
      { title: '全组织共用', desc: '跨组织对比供应商，让优质资源在全机构共享。', icon: 'sharing' },
      { title: '绩效驱动', desc: '以管理、品质、回应、交付、成本与服务绩效支撑分级奖惩。', icon: 'performance' },
      { title: '全生命周期治理', desc: '统一引入、合作、发展与退出规则，共享红线与黑名单。', icon: 'lifecycle' }
    ],
    apps: ['智能入驻', '智能风险查验', '智能 360 画像', '智能健康度'],
    process: {
      url: srmAsset('scene/supplier-management/process.webp'),
      alt: '供应商智能管理业务流程'
    },
    processMobile: {
      url: srmAsset('scene/supplier-management/mobile-process.webp'),
      alt: '供应商智能管理业务流程（移动端）'
    },
    demos: [
      {
        title: '供方AI全生命周期治理',
        lead: '覆盖供应商创建、入驻审批、风险查验、画像与健康分析，为全生命周期管理提供依据。',
        bullets: ['智能创建与入驻审核，核验准入资料', '智能查验风险，提示劣化趋势', '供应商360画像洞察，支持供方评估', '供应商健康度分析'],
        value: '统一供方视图，支撑准入与合作决策',
        media: { type: 'video', url: srmVideo('supplier-lifecycle') }
      }
    ]
  },
  {
    key: 'ai-native',
    order: 6,
    name: 'AI 原生方式',
    cardTitle: '安全可控·平滑演进',
    subtitle: '统一平台底座，支撑分阶段平滑演进',
    summary: '通过读懂业务意图，自动生成并适配应用，业务规格变、应用即变',
    hero: {
      url: srmAsset('scene/ai-native/hero.webp'),
      alt: 'AI 原生方式场景首屏'
    },
    cardImage: {
      url: srmAsset('overview/scene/ai-native.webp'),
      alt: '业务人员通过人工智能平台界面推进采购系统安全平滑演进'
    },
    cardTags: ['描述即实现', '需求零偏差', '演进快又稳', '多角色智能协同'],
    cardVariant: 'evolution',
    values: [
      { title: '统一平台底座', desc: '在统一底座上承载多角色智能协同，支撑复杂业务差异。', icon: 'platform' },
      { title: '读懂业务意图', desc: '以业务语言理解组织、规则、流程、字段与许可权。', icon: 'intent-understanding' },
      { title: '业务规格变，应用即变', desc: '自动生成并适配应用，减少硬编码与人工转译。', icon: 'adaptability' },
      { title: '分阶段平滑演进', desc: '保留历史数据与既有能力，按可控节奏持续演进。', icon: 'smooth-evolution' }
    ],
    apps: ['描述即实现', '需求零偏差', '演进快又稳'],
    process: {
      url: srmAsset('scene/ai-native/process.webp'),
      alt: 'AI 原生方式业务流程'
    },
    processMobile: {
      url: srmAsset('scene/ai-native/mobile-process.webp'),
      alt: 'AI 原生方式业务流程（移动端）'
    },
    demos: [
      {
        title: '统一平台支持平滑演进',
        lead: '通过读懂业务意图，自动生成并适配应用，业务规格变、应用即变。',
        bullets: ['描述即实现', '需求零偏差', '演进快又稳'],
        value: '不再靠硬编码实现，而是由平台以“业务语言”来理解和承载',
        media: { type: 'image', url: srmAsset('scene/ai-native/demo-01.webp') }
      }
    ]
  }
];

/** 按 order 升序排列的场景列表，顺序即一级页卡片顺序 */
export const scenes: SrmScene[] = [...sceneList].sort((a, b) => a.order - b.order);

/** 以 sceneKey 建立索引，供二级页与路由做参数校验 */
export const sceneMap: Record<SrmSceneKey, SrmScene> = scenes.reduce<Record<SrmSceneKey, SrmScene>>(
  (acc, scene) => {
    acc[scene.key] = scene;
    return acc;
  },
  {} as Record<SrmSceneKey, SrmScene>
);
